import { creerRuntime } from '@betool/identity-runtime'
import type { Runtime } from '@betool/identity-runtime'

import { clearSession } from '../api/client'
import { adressePorte } from './porte'

import {
  CLIENT_ID,
  acheterPreuve,
  lireJetonCsrf,
  origineBffIdentite,
  origineMoteurIdentite,
} from './bff'

/**
 * Le runtime d'identité — ce qui alimente le widget du rail.
 *
 * CE QU'IL EST, ET CE QU'IL N'EST PAS
 * ---------------------------------------------------------------------------
 * Le trousseau appartient au TENANT et vit scellé chez le BFF. Ce module ne le
 * détient pas : il ne connaît que des ADRESSES de slots — qui n'ouvrent rien
 * seules — et des preuves courtes, bornées par leur `exp`.
 *
 * La liste des comptes est légitime ICI, dans le menu d'une personne DÉJÀ
 * authentifiée, et elle ne l'était pas sur la page de connexion, qui est
 * publique. La différence n'est pas de degré : d'un côté on montre ses propres
 * comptes à quelqu'un qui vient de prouver qui il est, de l'autre on les
 * montrait à qui ouvrait l'URL.
 */

let runtime: Runtime | null = null

/**
 * Ouvre le runtime — AVANT le premier rendu, depuis `main.ts`.
 *
 * Synchrone et sans réseau : il doit être prêt quand le rail se monte. Sans
 * origine de BFF, il n'est PAS ouvert : parler en same-origin à un `/portail/*`
 * que betool-sign ne sert pas ne rendrait que des 404 déguisés en état vrai.
 * Tout ce module tolère un runtime nul — c'est son mode dégradé, pas une panne.
 */
export function ouvrirRuntimeIdentite(): Runtime | null {
  const origineBff = origineBffIdentite()
  if (origineBff === '') {
    console.warn(
      '[sign] VITE_BETOOL_IDENTITE_BFF absente : le widget d’identité ne sera pas monté.',
    )
    return null
  }
  runtime = creerRuntime({ lireJetonCsrf, origineBff })
  return runtime
}

/** Le runtime partagé — `null` tant que `main.ts` ne l'a pas ouvert. */
export function runtimeIdentite(): Runtime | null {
  return runtime
}

/**
 * Le fournisseur de porteur du widget.
 *
 * Appelé par le widget À CHAQUE requête — jamais mémorisé là-bas, c'est son
 * contrat. La mise en cache vit donc ici, et elle est COURTE par construction :
 * l'`exp` de la preuve la borne, moins une marge. Un porteur qui expirerait en
 * vol vaudrait 401 à l'arrivée.
 *
 * Le cache est clé par `slotRef` : un changement de compte dans cet onglet ne
 * peut pas servir la preuve de l'ancien.
 */
export function fournisseurPorteur(): () => string | Promise<string> {
  const MARGE_MS = 30_000
  let cache: { slotRef: string; accessToken: string; expireA: number } | null = null
  /** Promesse partagée : dix appels dans le même tic = UN seul achat. */
  let achatEnVol: Promise<string> | null = null
  let relecture: Promise<unknown> | null = null

  const porteurPour = (slotRef: string): string | Promise<string> => {
    if (cache !== null && cache.slotRef === slotRef && cache.expireA > Date.now()) {
      return cache.accessToken
    }
    if (achatEnVol === null) {
      achatEnVol = acheterPreuve(slotRef)
        .then((preuve) => {
          cache = {
            slotRef,
            accessToken: preuve.accessToken,
            expireA: Date.now() + preuve.expiresIn * 1000 - MARGE_MS,
          }
          return preuve.accessToken
        })
        .finally(() => {
          achatEnVol = null
        })
    }
    return achatEnVol
  }

  return () => {
    const r = runtimeIdentite()
    if (r === null) return ''

    const slot = r.currentSession()
    if (slot) return porteurPour(slot.slotRef)

    // Au tout premier rendu, l'onglet n'a pas encore relu ses slots : sans
    // cette réhydratation, le widget démarrerait « délié » alors qu'une session
    // existe, et n'y reviendrait qu'au prochain geste.
    if (relecture === null) {
      relecture = r.listBrowserSessions().catch(() => {
        relecture = null
      })
    }
    return relecture.then(() => {
      const relu = r.currentSession()
      return relu ? porteurPour(relu.slotRef) : ''
    })
  }
}

/** 404 ou 403 à l'émission du lancement de suivi : l'identité n'a PAS de compte ici. */
class SuiviRefuseError extends Error {
  constructor(readonly statut: number) {
    super(`lancement de suivi refusé (HTTP ${statut})`)
    this.name = 'SuiviRefuseError'
  }
}

/**
 * Fait SUIVRE l'application au compte choisi : un lancement complet vers
 * elle-même.
 *
 * L'ORDRE EST TRANCHÉ : la preuve s'achète SANS basculer, PUIS l'émission se
 * demande au moteur, PUIS — accordée seulement — l'onglet part. L'inverse
 * (basculer d'abord) montrerait le compte B dans le widget face à une
 * application encore ouverte sur le compte A.
 *
 * La session locale est effacée AVANT le départ : sans cela, l'arrivée
 * trouverait une session de l'ancien compte et pourrait la préférer au
 * lancement qu'elle vient de recevoir.
 */
async function suivreIdentite(slotRef: string): Promise<void> {
  const origineMoteur = origineMoteurIdentite()
  const preuve = await acheterPreuve(slotRef)

  let reponse: Response
  try {
    reponse = await fetch(
      `${origineMoteur}/api/applications/${encodeURIComponent(CLIENT_ID)}/lancement`,
      {
        method: 'POST',
        headers: {
          authorization: `Bearer ${preuve.accessToken}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify({ url: window.location.origin }),
        // `redirect: 'error'` : une redirection ici signifierait qu'on ne parle
        // plus au moteur, et suivre en aveugle enverrait la preuve ailleurs.
        redirect: 'error',
      },
    )
  } catch (erreur) {
    throw new Error(
      `lancement de suivi injoignable (${erreur instanceof Error ? erreur.message : 'inconnu'})`,
    )
  }

  // 404/403 : l'identité est VALIDE mais n'a pas d'accès à cette application.
  if (reponse.status === 404 || reponse.status === 403) throw new SuiviRefuseError(reponse.status)
  if (!reponse.ok) throw new Error(`lancement de suivi refusé (HTTP ${reponse.status})`)

  const brut = (await reponse.json().catch(() => null)) as Record<string, unknown> | null
  const jeton = typeof brut?.jeton === 'string' ? brut.jeton : ''
  const url = typeof brut?.url === 'string' ? brut.url : ''
  if (jeton === '' || url === '') throw new Error('lancement de suivi illisible')

  // Le moteur rend l'URL qu'il a validée contre ses origines enregistrées. La
  // comparer à la nôtre coûte une ligne et refuse net un départ vers ailleurs.
  if (url !== window.location.origin) {
    throw new Error('lancement de suivi vers une origine étrangère — refusé')
  }

  clearSession()
  window.location.replace(`${url}/?lancement=${encodeURIComponent(jeton)}`)
}

/**
 * La capacité MULTI-COMPTE du widget : la paire atomique (actions, liste).
 *
 * Sa présence seule fait passer le widget en mode multi-compte. `undefined`
 * quand le runtime est fermé — le widget reste alors mono-compte, ce qui est la
 * dégradation juste : il ne peut pas proposer ce qu'il ne peut pas lire.
 */
export function adaptateurComptes() {
  const r = runtimeIdentite()
  if (r === null) return undefined

  return {
    actions: {
      switchAccount: async (slotRef: string): Promise<void> => {
        // La liste AUTORITAIRE est relue au moment du geste : l'état a pu
        // changer dans un autre onglet depuis l'ouverture du panneau.
        const sessions = await r.listBrowserSessions()
        const cible = sessions.find((session) => session.slotRef === slotRef)

        // Une entrée « Expirée » n'a rien à activer : son clic n'est pas un
        // changement de compte, c'est une demande de RECONNEXION.
        if (cible?.state === 'expired') {
          clearSession()
          window.location.assign('/login')
          return
        }

        if (r.currentSession()?.slotRef === slotRef) return

        try {
          await suivreIdentite(slotRef)
        } catch (erreur) {
          // Politique d'accès « invitation » : contrairement au kiosk, il n'y a
          // PAS de page d'inscription vers laquelle conduire. Un accès se
          // demande à un administrateur — le dire, plutôt qu'ouvrir une porte
          // qui se refermerait.
          if (erreur instanceof SuiviRefuseError) {
            console.warn('[sign] ce compte n’a pas accès à beTool Sign :', erreur.message)
          }
          throw erreur
        }
      },
      // Les DEUX gestes, distincts et tous deux nécessaires : oublier le compte
      // sur CE navigateur (le slot quitte le trousseau, la session beTool
      // survit ailleurs) et fermer la session beTool elle-même. Les confondre
      // déconnecterait partout quelqu'un qui voulait nettoyer un poste partagé.
      forgetInThisBrowser: async (slotRef: string): Promise<void> => {
        await r.forgetInThisBrowser(slotRef)
      },
      /*
       * FERMER UNE SESSION SE FAIT CHEZ LE GARDIEN, jamais d'ici.
       *
       * Le trousseau est un cookie `__Host-`, attaché à l'origine EXACTE du
       * gardien : `r.signOut()` partait vers NOTRE origine et recevait 401, en
       * silence — le compte restait « Connectée » après qu'on l'ait quitté.
       * L'intention part donc avec une navigation, et l'adresse DÉSIGNE
       * l'entrée : le `slotRef` ne traverse pas, il est résolu là-bas
       * (ADR-016 §14.1-14.2).
       */
      signOut: async (slotRef: string): Promise<void> => {
        // La liste est RELUE : l'adresse est ce qu'on transmet, et elle a pu
        // changer dans un autre onglet depuis l'ouverture du panneau.
        const cible = (await r.listBrowserSessions()).find((s) => s.slotRef === slotRef)
        window.location.assign(adressePorte({ deconnexion: cible?.email ?? undefined }))
      },
      addAccount: (): void => {
        r.addAccount({ returnTo: window.location.origin })
      },
    },
    /**
     * La liste, telle que le runtime la tient.
     *
     * Le contrat du widget déclare un champ `eligibility` par identité, que
     * `BrowserSession` ne porte pas — il dira un jour si CE compte a accès à
     * CETTE application, pour router le clic (addendum 5, D3). Aucune ligne du
     * widget ne le lit aujourd'hui : vérifié dans ses sources, il n'apparaît
     * que dans sa déclaration de contrat.
     *
     * Le calculer pour de vrai demanderait une preuve et un appel PAR compte,
     * à chaque ouverture du panneau. On ne paie pas ce prix pour un champ que
     * personne ne consulte, et on ne l'INVENTE pas non plus : le marquer
     * « inscrit » ferait promettre un accès que le suivi refuserait ensuite.
     * Le cast dit exactement cela, et il tombera le jour où le widget routera.
     */
    list: () => r.listBrowserSessions() as unknown as Promise<
      readonly (import('@betool/identity-widget').AvailableIdentity)[]
    >,
  }
}
